// Config-only DS Table column for one custom field. Sorting uses an accessorFn over the typed sort value
// plus sortUndefined: 'last', so blanks stay at the bottom in both directions - see customFieldSortValue.
// Long values truncate to one line with an ellipsis, and the full value shows in a design-system hover box
// when it is cut off - see TruncatedValue.
// (Dist-internal imports are the accepted convention - see LinkColumn.)
import React from 'react';
import type {ColumnDef} from '@tanstack/react-table';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {defaultColumnDef} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/support/BaseColumn';
import {StyledCell, Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import {
	compareCustomFieldSortValues,
	customFieldSortValue,
	formatCustomFieldValue,
	type CustomFieldColumnDef,
} from '../../shared/components/custom-field-columns/customFieldColumnModel';
import type {OpportunitySortingFn, OpportunityTableRow} from '../opportunityTableRows';
import {HeaderName, TruncatedValue} from '../../shared/components/custom-field-columns/CustomFieldText';

export interface CustomFieldColumnType {
	id: string;
	header: string;
	definition: CustomFieldColumnDef;
	locale: string;
	// The viewer's decimal separator, so a number reads the way the inline editor parses it.
	decimalSeparator?: ',' | '.';
	visible?: boolean;
	width?: string;
	'data-cy'?: string;
	// Wraps the rendered value - the call site supplies the inline editor. Identity by default.
	cellContent?: (content: React.ReactNode, row: OpportunityTableRow) => React.ReactNode;
}

// Values are already typed per column; the model's comparator compares them for the column's kind (exact
// for numbers, which are kept as strings so long values do not round).
const byTypedValue = (definition: CustomFieldColumnDef): OpportunitySortingFn => {
	const compare = compareCustomFieldSortValues(definition);
	return (a, b, columnId) =>
		Math.sign(compare(a.getValue(columnId) as string | number, b.getValue(columnId) as string | number));
};

// Config-only component: DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CustomFieldColumn = (_: CustomFieldColumnType): null => null;

CustomFieldColumn.toColumnDef = (child: React.ReactElement<CustomFieldColumnType>): ColumnDef<OpportunityTableRow> => {
	const {definition, locale, decimalSeparator, cellContent} = child.props;
	// Every kind is centred under its header, the way the list's date, stage and status columns are.
	const align = Align.center;
	// allowSorting only picks the sortable header here; TanStack enables sorting because accessorFn is set.
	const name = child.props.header;
	const baseProps: Parameters<typeof defaultColumnDef>[0] & {allowSorting: boolean} = {
		id: child.props.id,
		header: (<HeaderName name={name} />) as unknown as string,
		visible: child.props.visible,
		width: child.props.width,
		'data-cy': child.props['data-cy'],
		align,
		allowSorting: true,
	};
	const base = defaultColumnDef(baseProps);
	return {
		...base,
		// The header cell renders the clamped label above; the table labels the column (aria-label) and
		// exports it from meta.header, which must stay the plain name.
		meta: {...base.meta, header: name},
		id: child.props.id,
		accessorFn: (row: OpportunityTableRow) => customFieldSortValue(definition, row.customFieldValues[definition.key]),
		sortingFn: byTypedValue(definition),
		sortUndefined: 'last',
		cell: ({row}: {row: {original: OpportunityTableRow}}) => {
			const display = formatCustomFieldValue(
				definition,
				row.original.customFieldValues[definition.key],
				locale,
				decimalSeparator
			);
			const content = display ? <TruncatedValue value={display} /> : null;
			const cellCy = child.props['data-cy'] ? `${child.props['data-cy']}-cell` : undefined;
			return (
				<StyledCell align={align} data-cy={cellCy}>
					{cellContent ? cellContent(content, row.original) : content}
				</StyledCell>
			);
		},
	};
};

export default CustomFieldColumn;
